import { config, storageKey } from "../config";

type MetaConfig = { enabled: boolean; pixelEnabled: boolean; capiEnabled: boolean; pixelId: string; revision: number; mode: "test" | "live"; events: Record<string, string> };
type Consent = { granted: boolean | null; revision: number };
type MetaState = { configuration: MetaConfig | null; consent: Consent; error: string };
export type MetaCandidate = { id: string; type: string; eventTime: number; consentRevision: number };
type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue: unknown[][]; push: Fbq; loaded: boolean; version: string };
declare global { interface Window { fbq?: Fbq; _fbq?: Fbq } }
const deniedKey = storageKey("meta-denied");
let state: MetaState = { configuration: null, consent: { granted: null, revision: 0 }, error: "" };
let prepared: Promise<void> | null = null;
let session: { sessionId: string; source?: string; displayMode: string } | null = null;
let pixelInitialized = "";
let sending = false, failures = 0;
let retry: ReturnType<typeof setTimeout> | undefined;
let consentChannel: BroadcastChannel | null = null;
const sent = new Set<string>(), queued: MetaCandidate[] = [];
const listeners = new Set<() => void>();
const click = new URLSearchParams(location.search).get("fbclid");
const clickTime = Date.now();
export const getMetaState = () => state;
export const subscribeMeta = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
function update(next: Partial<MetaState>) { state = { ...state, ...next }; listeners.forEach(fn => fn()); }
function locallyDenied() { try { return localStorage.getItem(deniedKey) === "1"; } catch { return false; } }
async function api(path: string, body?: object, method = "POST") {
  const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(`${config.apiBaseUrl}/v1/pwa/meta/${path}`, { method: body ? method : "GET", cache: "no-store", credentials: "same-origin", signal: controller.signal,
      headers: body ? { "Content-Type": "application/json" } : undefined, body: body ? JSON.stringify(body) : undefined });
    if (!response.ok) throw new Error(String(response.status));
    return await response.json();
  } finally { clearTimeout(timeout); }
}
function stop() {
  queued.length = 0; clearTimeout(retry);
  const fbq = window.fbq;
  if (fbq) {
    // Discard unsent tracking calls before a later grant could replay them.
    if (fbq.queue) fbq.queue = fbq.queue.filter(args => !String(args[0]).startsWith("track"));
    fbq("consent", "revoke");
  }
}
export function resetMetaSession() { stop(); session = null; prepared = null; update({ consent: { granted: null, revision: 0 } }); }
function removeMetaCookies() {
  const host = location.hostname.split(".");
  for (const name of ["_fbp", "_fbc"]) {
    document.cookie = `${name}=; Path=/; Max-Age=0`;
    for (let i = 0; i < host.length - 1; i++) document.cookie = `${name}=; Path=/; Max-Age=0; Domain=${host.slice(i).join(".")}`;
  }
}
function pixel() {
  const c = state.configuration;
  if (!c?.pixelEnabled || state.consent.granted !== true || locallyDenied()) return null;
  if (!window.fbq) {
    const q = function (...args: unknown[]) { if (q.callMethod) q.callMethod(...args); else q.queue.push(args); } as Fbq;
    q.queue = []; q.push = q; q.loaded = true; q.version = "2.0";
    window.fbq = q; window._fbq = q;
    q("consent", "revoke");
    const script = document.createElement("script"); script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.append(script);
  }
  const q = window.fbq;
  if (pixelInitialized !== c.pixelId) {
    q("set", "autoConfig", false, c.pixelId);
    q("init", c.pixelId);
    pixelInitialized = c.pixelId;
  }
  q("consent", "grant");
  return q;
}
export async function prepareMeta() {
  if (window.top !== window.self) return;
  if (!prepared) prepared = (async () => {
    const configuration = await api("config") as MetaConfig;
    const consent = configuration.enabled ? await api("consent") as Consent : { granted: null, revision: 0 };
    if (locallyDenied() && configuration.enabled && consent.granted === true) {
      try { Object.assign(consent, await api("consent", { granted: false }, "PUT")); } catch { consent.granted = false; }
    }
    update({ configuration, consent, error: "" });
    if (!configuration.enabled || consent.granted !== true) stop();
  })().catch(() => { prepared = null; stop(); update({ configuration: null }); });
  return prepared;
}
export async function chooseMetaConsent(granted: boolean) {
  if (!granted) {
    try { localStorage.setItem(deniedKey, "1"); } catch { /* Server choice remains authoritative. */ }
    update({ consent: { ...state.consent, granted: false } }); stop(); removeMetaCookies();
  }
  try {
    const consent = await api("consent", { granted }, "PUT") as Consent;
    if (granted) { try { localStorage.removeItem(deniedKey); } catch { /* No local override. */ } }
    update({ consent, error: "" });
    consentChannel?.postMessage("changed");
    if (granted) startPageEvents();
  } catch {
    update({ error: granted ? "无法保存选择，请稍后重试。" : "当前页面已停止广告统计；联网后会同步关闭服务器回传。" });
  }
}
export function captureMeta(type: string, id: string, eventTime = Math.floor(Date.now() / 1000)): MetaCandidate | undefined {
  const c = state.configuration;
  if (!session || !c?.enabled || !c.events[type] || state.consent.granted !== true || locallyDenied()) return;
  const key = `${c.pixelId}:${type}:${id}`;
  if (sent.has(key)) return;
  sent.add(key);
  const q = pixel();
  if (q) q(["page_view", "complete_registration"].includes(type) ? "trackSingle" : "trackSingleCustom", c.pixelId, c.events[type], { channel: session.source || "direct" }, { eventID: `yestv_${config.environment}_${id}` });
  return { id, type, eventTime, consentRevision: state.consent.revision };
}
export function registrationMetaContext() {
  if (!session || state.consent.granted !== true || locallyDenied()) return undefined;
  return { consentRevision: state.consent.revision, eventSourceUrl: location.origin + location.pathname, ...matchingCookies() };
}
export function trackRegistration(receipt: { id: string; eventTime: number; sessionId: string } | null) {
  if (!receipt || receipt.sessionId !== session?.sessionId) return;
  deliverMeta(captureMeta("complete_registration", receipt.id, receipt.eventTime));
}
export function deliverMeta(candidate?: MetaCandidate) {
  if (!candidate || !state.configuration?.capiEnabled || state.consent.granted !== true || locallyDenied() || candidate.consentRevision !== state.consent.revision || queued.length >= 100) return;
  queued.push(candidate); void flush();
}
function readCookie(name: string) {
  try { return decodeURIComponent(document.cookie.split("; ").find(v => v.startsWith(name + "="))?.slice(name.length + 1) || ""); } catch { return ""; }
}
function matchingCookies() {
  let fbc = readCookie("_fbc");
  // Build fbc only from an actual fbclid. Never invent a click ID or a browser ID.
  if (click && /^[a-zA-Z0-9_.-]{1,500}$/.test(click)) {
    if (!fbc.endsWith("." + click)) {
      fbc = `fb.1.${clickTime}.${click}`;
      document.cookie = `_fbc=${encodeURIComponent(fbc)}; Path=/; Max-Age=7776000; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
    }
  }
  const valid = (value: string) => /^fb\.\d{1,2}\.\d{13}\.[a-zA-Z0-9_.-]+$/.test(value) && value.length <= 600 ? value : "";
  return { fbp: valid(readCookie("_fbp")), fbc: valid(fbc) };
}
async function flush() {
  if (sending || !queued.length || !session || state.consent.granted !== true || locallyDenied()) return;
  sending = true;
  const batch = queued.slice(0, 20);
  try {
    await api("events", { sessionId: session.sessionId, consentRevision: batch[0].consentRevision, eventSourceUrl: location.origin + location.pathname,
      ...matchingCookies(), events: batch.map(({ id, type, eventTime }) => ({ id, type, eventTime })) });
    for (const item of batch) { const index = queued.indexOf(item); if (index !== -1) queued.splice(index, 1); } failures = 0;
  } catch (error) {
    if (error instanceof Error && ["401", "403", "422"].includes(error.message)) { for (const item of batch) { const index = queued.indexOf(item); if (index !== -1) queued.splice(index, 1); } }
    failures++;
  } finally {
    sending = false;
    if (queued.length && failures < 4 && state.consent.granted === true) { clearTimeout(retry); retry = setTimeout(() => void flush(), failures ? failures * 2000 : 0); }
  }
}
function startPageEvents() {
  if (!session) return;
  deliverMeta(captureMeta("page_view", session.sessionId));
  if (session.displayMode === "standalone") deliverMeta(captureMeta("home_open", session.sessionId));
}
export function startMetaSession(value: { sessionId: string; source?: string; displayMode: string }) { session = value; startPageEvents(); }
async function refreshConsent() {
  if (!session) return;
  prepared = null; await prepareMeta(); startPageEvents();
}
addEventListener("focus", () => void refreshConsent());
addEventListener("online", () => { failures = 0; void refreshConsent().then(flush); });
addEventListener("storage", e => { if (e.key === deniedKey && e.newValue === "1") { update({ consent: { ...state.consent, granted: false } }); stop(); } });
try { consentChannel = new BroadcastChannel("yestv-meta-consent"); consentChannel.onmessage = () => void refreshConsent(); } catch { /* iOS fallback: focus/online. */ }
